import { Router } from 'vue-router';
type EmitType = {
  (event: 'page-change', value: number): void;
};

interface PaginationProps {
  postsPerPage: number;
  totalItems: Ref<number>;
  router: Router;
  emit: EmitType;
  pageQueryName: 'page';
}

export default function usePagination({
  postsPerPage,
  totalItems,
  router,
  emit,
  pageQueryName,
}: PaginationProps) {
  const firstPage = ref(1);
  const pagesVisible = ref(0);

  const totalPages = computed(() => {
    return Math.ceil(totalItems.value / postsPerPage);
  });

  const pageCount = computed(() => {
    return Array.from({ length: totalPages.value }, (_, index) => index + 1);
  });
  const currentPage = computed(
    () =>
      parseInt(router.currentRoute.value.query[pageQueryName] as string) || 1,
  );
  const goToPage = (page: string | number) => {
    if (typeof page === 'number' && page >= 1 && page <= totalPages.value) {
      emit('page-change', page);

      // Update the route with the new page
      router.push({
        query: {
          [pageQueryName]: page,
          s: router.currentRoute.value.query.s,
        },
      });
    }
  };

  const updateVisiblePages = () => {
    pagesVisible.value = window.innerWidth >= 768 ? 5 : 3;
  };

  const visiblePageNumbers = computed(() => {
    const firstMiddlePage = Math.ceil(pagesVisible.value / 2);
    const lastMiddlePage = totalPages.value - firstMiddlePage;

    let pageNumbers = [];
    const totalArr = Array.from({ length: totalPages.value }, (_, i) => i + 1);

    if (totalPages.value <= pagesVisible.value) {
      pageNumbers = totalArr;
    } else {
      if (currentPage.value <= firstMiddlePage) {
        pageNumbers = totalArr.slice(0, pagesVisible.value);
      } else if (currentPage.value >= lastMiddlePage + 1) {
        pageNumbers = totalArr.slice(totalPages.value - pagesVisible.value);
      } else {
        pageNumbers = totalArr.slice(
          currentPage.value - pagesVisible.value / 2,
          currentPage.value + pagesVisible.value / 2,
        );
      }
    }

    return pageNumbers;
  });

  const showEllipsisBefore = computed(
    () => currentPage.value > (pagesVisible.value === 3 ? 3 : 4),
  );

  const showEllipsisAfter = computed(
    () =>
      totalPages.value > pagesVisible.value &&
      currentPage.value < totalPages.value - (pagesVisible.value === 3 ? 2 : 3),
  );

  const showFirstPage = computed(
    () => currentPage.value > (pagesVisible.value === 3 ? 2 : 3),
  );

  const showLastPageLink = computed(
    () =>
      currentPage.value < totalPages.value - (pagesVisible.value === 3 ? 1 : 2),
  );

  onMounted(() => {
    window.addEventListener('resize', updateVisiblePages);
    updateVisiblePages();

    // Update the route with the new page
    router.push({
      query: {
        [pageQueryName]: currentPage.value,
        s: router.currentRoute.value.query.s,
      },
    });
  });
  return {
    firstPage,
    currentPage,
    totalPages,
    pageCount,
    goToPage,
    showFirstPage,
    showEllipsisBefore,
    visiblePageNumbers,
    showEllipsisAfter,
    showLastPageLink,
  };
}
